import {
  Component,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  AfterViewInit,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatGridListModule } from "@angular/material/grid-list";
import { MatMenuModule } from "@angular/material/menu";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { TranslateModule } from "@ngx-translate/core";
import { MatTableModule } from "@angular/material/table";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSortModule, MatSort } from "@angular/material/sort";
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import { MatSelectModule } from "@angular/material/select";
import { MatOptionModule } from "@angular/material/core";
import {
  MatPaginator,
  MatPaginatorModule
} from "@angular/material/paginator";
import { DatePipe } from "@angular/common";
import { MatTableDataSource } from "@angular/material/table";
import { debounceTime } from "rxjs";
import * as Dashboards from "@highcharts/dashboards";
import Highcharts from "highcharts";
import DataGrid from "@highcharts/dashboards/datagrid";
import LayoutModule from "@highcharts/dashboards/modules/layout";
import { HighchartsChartModule } from "highcharts-angular";
import HC_Exporting from "highcharts/modules/exporting";
import HC_ExportingOffline from "highcharts/modules/offline-exporting";
import HC_ExportData from "highcharts/modules/export-data";
import HC_More from "highcharts/highcharts-more";
import HC_data from "highcharts/modules/data";
import { ApplicationDataService } from "../../core/services/application-data.service";
import { Router } from "@angular/router";

interface Complaint {
  id: number;
  category: string;
  agent: string;
  status: string;
  // add other properties
}
type RawCSRData = [string, number, number];

interface CSRData {
  csr: string;
  totalComplaints: number;
  avgResponseTime: number;
}

@Component({
  selector: "app-dashboard-three",
  standalone: true,
  imports: [
    MatCardModule,
    HighchartsChartModule,
    CommonModule,
    MatSelectModule,
    MatOptionModule,
    MatGridListModule,
    MatMenuModule,
    MatIconModule,
    MatButtonModule,
    TranslateModule,
    MatTableModule,
    MatSortModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    FormsModule,
    ReactiveFormsModule,
    MatPaginatorModule,
  ],
  templateUrl: "./dashboard-three.component.html",
  styleUrl: "./dashboard-three.component.css",
})
export class DashboardThreeComponent implements OnInit, AfterViewInit, OnDestroy {
  private highchartsStyleEl: HTMLLinkElement | null = null;
  rawData: any[] = [];
  dataSource: MatTableDataSource<any>;
  detailsDisplayedColumns = [
    "csr",
    "complaintNo",
    "receivedDate",
    "closedDate",
    "avgResponseTime",
    "complainantType",
    "complaintType",
    "action",
  ];

  // Add these for CSR table pagination
  csrDataSource = new MatTableDataSource<any>([]);
  csrDisplayedColumns = ["csr", "totalComplaints", "avgResponseTime"];
  csrConfig = {
    pageSizeOptionsList: [5, 10, 25, 50],
  };

  detailsConfig = {
    pageSizeOptionsList: [10, 20, 50, 100],
  };
  // Details table pagination (UPDATED)
  detailsDataSource = new MatTableDataSource<any>([]);
  private options = {};
  private readonly isAsync = true;
  private isInitialLoad = true;
  private isLoading = false;
  private originalCSRData: any[][] = [];
  private originalDetailsData: any[][] = [];

  totalCountofComplaintbCategoryChartData: any[] = [];
  totalCountofComplaintbyCategoryChartDataCategories: any[] = [];
  totalCountofComplaintbyAgent: any[] = [];
  totalComplaintsByAgentDetailCount: any[] = [];
  csrSortState: { column: string; direction: 'asc' | 'desc' | '' } = { column: '', direction: '' };

  selectedCategory: string = "";
  selectedUserName: string = "";
  name: string = "";
  form: FormGroup;
  pageNo = 0;
  pageSize = 10;
  length = 0;
  sortState: 0 | 1 | 2 = 0;
  sortStateDetailsByType: 0 | 1 | 2 = 0;
  sortStateDetailsByComplaintType: 0 | 1 | 2 = 0;
  sortStateDetailsByRespondent: 0 | 1 | 2 = 0;

  userRole: string;

  @ViewChild(MatSort) sort: MatSort;
  @ViewChild("searchCurrentProcess")
  searchCurrentProcess: ElementRef<HTMLInputElement>;
  @ViewChild(MatPaginator) csrPaginator!: MatPaginator;
  @ViewChild(MatSort) csrSort!: MatSort;
  @ViewChild('detailsPaginator', { static: false }) detailsPaginator!: MatPaginator;

  @ViewChild("detailsSort") detailsSort!: MatSort;

  constructor(
    public elementRef: ElementRef,
    private appDataService: ApplicationDataService,
    private formBuilder: FormBuilder,
    private datePipe: DatePipe,
    private router: Router
  ) {
    this.dataSource = new MatTableDataSource<Complaint>([]);
    this.csrDataSource = new MatTableDataSource<any>([]);
    this.detailsDataSource = new MatTableDataSource<any>([]);
  }

  ngOnInit(): void {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'assets/highcharts.css';
    link.id = 'highcharts-theme-css';
    document.head.appendChild(link);
    this.highchartsStyleEl = link;

    this.initializeHighcharts();
    this.initializeForm();
    this.setupDateChangeSubscriptions();
    this.initializeDashboard();
    this.loadUserid();
    this.loadInitialData();
  }
  ngOnDestroy(): void {
    this.highchartsStyleEl?.remove();
    this.highchartsStyleEl = null;
  }

  ngAfterViewInit(): void {
    if (!this.dataSource) {
      this.dataSource = new MatTableDataSource<Complaint>([]);
    }
    this.dataSource.sort = this.sort;
    this.dataSource.sortingDataAccessor = (item, property) => {
      const value = item[property];
      return typeof value === "string" ? value.toLowerCase() : value;
    };

    this.detailsDataSource.sort = this.detailsSort;
    this.detailsDataSource.sortingDataAccessor = (row, property) => {
      switch (property) {
        case "csr": // Respondent column (row[0])
          return (row[0] || "").toLowerCase();
        case "complaintNo": // Complaint No. column (row[1])
          return (row[1] || "").toLowerCase();
        case "receivedDate": // Lodge Date column (row[3]) - FIXED INDEX
          return new Date(row[3] || "").getTime() || 0;
        case "closedDate": // First Response Date column (row[4]) - FIXED INDEX
          return new Date(row[4] || "").getTime() || 0;
        case "avgResponseTime": // Duration column (row[5]) - FIXED INDEX
          return (row[5] || "").toLowerCase();
        case "complainantType": // Complainant Type column (row[6]) - FIXED INDEX
          return (row[6] || "").toLowerCase();
        case "complaintType": // Complaint Type column (row[7]) - FIXED INDEX
          return (row[7] || "").toLowerCase();
        case "action": // Action column
          return "";
        default:
          return "";
      }
    };

    setTimeout(() => {
      this.setupCSRTablePagination();
      this.setupDetailsTablePagination();
    }, 0);
  }

  viewComplaint(complaint: any) {
    // Use index 2 which contains the complaint ID (33 in your example)
    const complaintId = complaint[2];

    if (complaintId) {
      this.router.navigate(["complaint/edit", complaintId]);
    } else {
      console.error("Complaint ID not found at index 2:", complaint);
    }
  }

  private refreshCSRTable(): void {
    if (this.csrDataSource) {
      this.csrDataSource._updateChangeSubscription();
    }

    this.csrDataSource.connect().subscribe((data) => {
      // console.log("CSR DataSource changed:", data);
    });
  }

  // ADDED: Method to refresh details table
  private refreshDetailsTable(): void {
    if (this.detailsDataSource) {
      this.detailsDataSource._updateChangeSubscription();
    }

    this.detailsDataSource.connect().subscribe((data) => {
      //console.log("Details DataSource changed:", data);
      console.log("Details DataSource changed:");
    });
  }

  // Helper method to get properly sorted CSR data
  private getSortedCSRData(data: any[]): any[] {
    if (
      !this.csrSort ||
      !this.csrSort.active ||
      this.csrSort.direction === ""
    ) {
      return data;
    }

    return data.slice().sort((a, b) => {
      const isAsc = this.csrSort.direction === "asc";
      const valueA = this.getCSRSortValue(a, this.csrSort.active);
      const valueB = this.getCSRSortValue(b, this.csrSort.active);

      return this.compare(valueA, valueB, isAsc);
    });
  }

  // ADDED: Helper method to get properly sorted details data
  private getSortedDetailsData(data: any[]): any[] {
    if (
      !this.detailsSort ||
      !this.detailsSort.active ||
      this.detailsSort.direction === ""
    ) {
      return data;
    }

    return data.slice().sort((a, b) => {
      const isAsc = this.detailsSort.direction === "asc";
      const valueA = this.getDetailsSortValue(a, this.detailsSort.active);
      const valueB = this.getDetailsSortValue(b, this.detailsSort.active);

      return this.compare(valueA, valueB, isAsc);
    });
  }

  // Helper method to get sort value from CSR data (array format)
  private getCSRSortValue(item: any[], column: string): string | number {
    switch (column) {
      case "csr":
        return (item[0] || "").toLowerCase();
      case "totalComplaints":
        return Number(item[1]) || 0;
      case "avgResponseTime":
        return (item[2] || "").toLowerCase();
      default:
        return "";
    }
  }

  // UPDATED: Helper method to get sort value from details data (array format) - complete implementation
  private getDetailsSortValue(item: any[], column: string): string | number {
    switch (column) {
      case "csr": // Respondent column (row[0])
        return (item[0] || "").toLowerCase();
      case "complaintNo": // Complaint No. column (row[1])
        return (item[1] || "").toLowerCase();
      case "receivedDate": // Lodge Date column (row[3]) - FIXED INDEX
        return new Date(item[3] || "").getTime() || 0;
      case "closedDate": // First Response Date column (row[4]) - FIXED INDEX
        return new Date(item[4] || "").getTime() || 0;
      case "avgResponseTime": // Duration column (row[5]) - FIXED INDEX
        return (item[5] || "").toLowerCase();
      case "complainantType": // Complainant Type column (row[6]) - FIXED INDEX
        return (item[6] || "").toLowerCase();
      case "complaintType": // Complaint Type column (row[7]) - FIXED INDEX
        return (item[7] || "").toLowerCase();
      case "action":
        return "";
      default:
        return "";
    }
  }
  // Helper method to compare values
  private compare(
    a: string | number,
    b: string | number,
    isAsc: boolean
  ): number {
    if (a < b) {
      return isAsc ? -1 : 1;
    }
    if (a > b) {
      return isAsc ? 1 : -1;
    }
    return 0;
  }

  // Check if export should be disabled for CSR table
  get isCSRExportDisabled(): boolean {
    return (
      !this.csrDataSource.filteredData ||
      this.csrDataSource.filteredData.length === 0
    );
  }

  // ADDED: Check if export should be disabled for details table
  get isDetailsExportDisabled(): boolean {
    return (
      !this.detailsDataSource.filteredData ||
      this.detailsDataSource.filteredData.length === 0
    );
  }

  // FIXED: Update the method that loads CSR data - keep array format for template compatibility
  private updateCSRDataSource(data: any[]): void {
    const processed = data.map((item) =>
      Array.isArray(item)
        ? item
        : [
          item.csr || "",
          item.totalComplaints || 0,
          item.avgResponseTime || "",
        ]
    );

    // save original
    this.originalCSRData = [...processed];
    // initialize table
    this.csrDataSource.data = [...processed];

    // re‑attach paginator & sort after view init
    setTimeout(() => {
      this.csrDataSource.paginator = this.csrPaginator;
      this.csrDataSource.sort = this.csrSort;
    });
  }

  // ADDED: Method to update details data source with pagination support
  private updateDetailsDataSource(data: any[]): void {
    const processed = data.map((item) =>
      Array.isArray(item)
        ? item
        : [
          item.csr || "",
          item.complaintNo || "",
          item.receivedDate || "",
          item.closedDate || "",
          item.avgResponseTime || "",
          item.complainantType || "",
          item.someOtherField || "",
          item.complaintType || "",
        ]
    );
    this.originalDetailsData = [...processed];

    // First set data
    this.detailsDataSource.data = [...processed];

    setTimeout(() => {
      this.detailsDataSource.paginator = this.detailsPaginator;
      this.detailsDataSource.sort = this.detailsSort;
    }, 0);

  }
  // FIXED: Modified existing methods to properly update CSR data source
  getAllAgentsCount(): void {
    const dateFrom = this.form.value.dateFrom
      ? this.datePipe.transform(this.form.value.dateFrom, "yyyy-MM-dd")
      : null;
    const dateTo = this.form.value.dateTo
      ? this.datePipe.transform(this.form.value.dateTo, "yyyy-MM-dd")
      : null;

    const payload = {
      name: "",
      complaintType: "",
      dateFrom: dateFrom,
      dateTo: dateTo,
    };

    //console.log("getAllAgentsCount payload:", payload);

    this.appDataService.getTotalComplaintsByAgentCount(payload).subscribe({
      next: (response: any) => {
        const data = response?.data ?? [];
       // console.log("getAllAgentsCount response:", data);

        // FIXED: Use the helper method to properly update data source
        this.updateCSRDataSource(data);

        this.setOptions();
        this.renderDashboard();
      },
      error: (error) => this.handleApiError(error, "getAllAgentsCount"),
    });
  }

  getTotalComplaintsByAgentCount(category: string): void {
    const dateFrom = this.form.value.dateFrom
      ? this.datePipe.transform(this.form.value.dateFrom, "yyyy-MM-dd")
      : null;
    const dateTo = this.form.value.dateTo
      ? this.datePipe.transform(this.form.value.dateTo, "yyyy-MM-dd")
      : null;

    if (!category) {
      console.error("Category is required for getTotalComplaintsByAgentCount");
      return;
    }

    const payload = {
      name: "",
      complaintType: category,
      dateFrom: dateFrom,
      dateTo: dateTo,
    };

   // console.log("getTotalComplaintsByAgentCount payload:", payload);

    this.appDataService.getTotalComplaintsByAgentCount(payload).subscribe({
      next: (response: any) => {
        const data = response?.data ?? [];
      //  console.log("getTotalComplaintsByAgentCount response:", data);

        // FIXED: Use the helper method to properly update data source
        this.updateCSRDataSource(data);

        this.setOptions();
        this.renderDashboard();
      },
      error: (error) =>
        this.handleApiError(error, "getTotalComplaintsByAgentCount"),
    });
  }

  getTotalComplaintsByAgentCountForCSR(
    category: string,
    userName: string
  ): void {
    const dateFrom = this.form.value.dateFrom
      ? this.datePipe.transform(this.form.value.dateFrom, "yyyy-MM-dd")
      : null;
    const dateTo = this.form.value.dateTo
      ? this.datePipe.transform(this.form.value.dateTo, "yyyy-MM-dd")
      : null;

    const payload = {
      name: userName,
      complaintType: category,
      dateFrom: dateFrom,
      dateTo: dateTo,
    };

   // console.log("getTotalComplaintsByAgentCountForCSR payload:", payload);

    this.appDataService.getTotalComplaintsByAgentCount(payload).subscribe({
      next: (response: any) => {
        const data = response?.data ?? [];
       // console.log("getTotalComplaintsByAgentCountForCSR response:", data);

        // FIXED: Use the helper method to properly update data source
        this.updateCSRDataSource(data);

        this.setOptions();
        this.renderDashboard();
      },
      error: (error) =>
        this.handleApiError(error, "getTotalComplaintsByAgentCountForCSR"),
    });
  }

  // Rest of your existing methods remain the same...
  private initializeHighcharts(): void {
    LayoutModule(Dashboards);
    HC_data(Highcharts);
    HC_Exporting(Highcharts);
    HC_ExportingOffline(Highcharts);
    HC_ExportData(Highcharts);
    HC_More(Highcharts);

    Dashboards.HighchartsPlugin.custom.connectHighcharts(Highcharts);
    Dashboards.DataGridPlugin.custom.connectDataGrid(DataGrid);

    Dashboards.PluginHandler.addPlugin(Dashboards.HighchartsPlugin);
    Dashboards.PluginHandler.addPlugin(Dashboards.DataGridPlugin);
  }

  private initializeForm(): void {
    this.form = this.formBuilder.group({
      dateFrom: [null],
      dateTo: [null],
      complainantName: [""],
      complaintNo: [""],
      complaintCategoryId: [""],
      complaintSubcategoryId: [""],
      currentProcessId: [""],
    });
  }

  // FIXED: Improved date change subscriptions with proper loading states
  private setupDateChangeSubscriptions(): void {
    this.f["dateFrom"].valueChanges.pipe(debounceTime(300)).subscribe(() => {
      if (!this.isLoading) {
        this.isLoading = true;
        this.resetPagination();
        this.loadAllData();
        // Add a small delay to ensure proper loading
        setTimeout(() => {
          this.isLoading = false;
        }, 500);
      }
    });

    this.f["dateTo"].valueChanges.pipe(debounceTime(300)).subscribe(() => {
      if (!this.isLoading) {
        this.isLoading = true;
        this.resetPagination();
        this.loadAllData();
        // Add a small delay to ensure proper loading
        setTimeout(() => {
          this.isLoading = false;
        }, 500);
      }
    });
  }

  private initializeDashboard(): void {
    // Dashboard initialization moved to separate method for clarity
  }

  private loadInitialData(): void {
    this.getTotalComplaintsByCategoryCount();
    this.getAllAgentsCount();
    this.getTotalComplaintsByAgentDetailCount();
  }

  private loadAllData(): void {
    this.getTotalComplaintsByCategoryCount();
    this.getAllAgentsCount();
    this.getTotalComplaintsByAgentDetailCount();
  }

  get f(): { [key: string]: AbstractControl } {
    return this.form.controls;
  }

  resetPagination(): void {
    this.pageNo = 0;
    this.rawData = [];
    this.length = 0;
  }

  private renderDashboard(): void {
    try {
      Dashboards.board("container", this.options, this.isAsync);
    } catch (error) {
      console.error("Error rendering dashboard:", error);
    }
  }

  onRowClick(userName: any): void {
    this.selectedUserName = userName;
    this.getAgentDetailCount(this.selectedCategory, userName);
  }

  formatDuration(duration: any): string {
    if (!duration) return "";

    if (typeof duration === "object" && duration.type === "interval") {
      const days = duration.days || 0;
      const hours = duration.hours || 0;
      const totalHours = hours;

      return `${days.toString().padStart(2, "0")} Days:${totalHours
        .toString()
        .padStart(2, "0")} Hours`;
    }

    if (typeof duration === "string" && duration.includes(":")) {
      return duration;
    }

    if (typeof duration === "number") {
      const hours = Math.floor(duration / 60);
      const days = duration;
      return `${hours.toString().padStart(2, "0")}:${days
        .toString()
        .padStart(2, "0")}`;
    }

    if (typeof duration === "string" && !isNaN(Number(duration))) {
      const totalDays = Number(duration);
      const hours = Math.floor(totalDays);
      const days = totalDays;
      return `${hours.toString().padStart(2, "0")}:${days
        .toString()
        .padStart(2, "0")}`;
    }

    return duration.toString();
  }

  // FIXED: Improved CSR table pagination setup (array format)
  private setupCSRTablePagination(): void {
    if (this.csrPaginator && this.csrSort) {
      this.csrDataSource.paginator = this.csrPaginator;
      this.csrDataSource.sort = this.csrSort;

      // FIXED: Custom sorting for CSR table with array format
      this.csrDataSource.sortingDataAccessor = (item, property) => {
        switch (property) {
          case "csr":
            return (item[0] || "").toLowerCase();
          case "totalComplaints":
            return Number(item[1]) || 0;
          case "avgResponseTime":
            return (item[2] || "").toLowerCase();
          default:
            return "";
        }
      };

      // If we already have data, update the table
      if (this.totalCountofComplaintbyAgent.length > 0) {
        this.updateCSRDataSource(this.totalCountofComplaintbyAgent);
      }
    }
  }

  // ADDED: Setup details table pagination similar to CSR table
  // UPDATED: Setup details table pagination with complete column sorting
  private setupDetailsTablePagination(): void {
    if (this.detailsPaginator && this.detailsSort) {
      this.detailsDataSource.paginator = this.detailsPaginator;
      this.detailsDataSource.sort = this.detailsSort;

      this.detailsDataSource.sortingDataAccessor = (item, property) => {
        switch (property) {
          case "csr": // Respondent column (row[0])
            return (item[0] || "").toLowerCase();
          case "complaintNo": // Complaint No. column (row[1])
            return (item[1] || "").toLowerCase();
          case "receivedDate": // Lodge Date column (row[3]) - FIXED INDEX
            return new Date(item[3] || "").getTime() || 0;
          case "closedDate": // First Response Date column (row[4]) - FIXED INDEX
            return new Date(item[4] || "").getTime() || 0;
          case "avgResponseTime": // Duration column (row[5]) - FIXED INDEX
            return (item[5] || "").toLowerCase();
          case "complainantType": // Complainant Type column (row[6]) - FIXED INDEX
            return (item[6] || "").toLowerCase();
          case "complaintType": // Complaint Type column (row[7]) - FIXED INDEX
            return (item[7] || "").toLowerCase();
          case "action":
            return "";
          default:
            return "";
        }
      };

      if (this.totalComplaintsByAgentDetailCount.length > 0) {
        this.updateDetailsDataSource(this.totalComplaintsByAgentDetailCount);
      }
    }
  }

  // FIXED: Improved slice click handler with proper data refresh
  onSliceClick(event: any): void {
    if (this.userRole === 'DASHBOARD') {
      return;
    }
    this.selectedCategory = event?.point?.name;
   // console.log("Pie slice clicked, category:", this.selectedCategory);

    // FIXED: Ensure we refresh both CSR and detail data when category is selected
    if (this.selectedCategory) {
      this.getTotalComplaintsByAgentCount(this.selectedCategory);
      this.getAgentDetailCount(this.selectedCategory);
    }
    this.selectedUserName = "";
  }

  resetCategory(): void {
    this.selectedCategory = "";
    this.selectedUserName = "";
   // console.log("Resetting category and refreshing all data");

    // FIXED: Ensure all data is refreshed when category is reset
    this.getTotalComplaintsByCategoryCount();
    this.getAllAgentsCount();
    this.getTotalComplaintsByAgentDetailCount();
  }

  resetUserSelection(): void {
    this.selectedUserName = "";
    if (this.selectedCategory) {
      this.getAgentDetailCount(this.selectedCategory);
    } else {
      this.getTotalComplaintsByAgentDetailCount();
    }
  }

  getTotalComplaintsByCategoryCount(): void {
    const dateFrom = this.form.value.dateFrom
      ? this.datePipe.transform(this.form.value.dateFrom, "yyyy-MM-dd")
      : null;
    const dateTo = this.form.value.dateTo
      ? this.datePipe.transform(this.form.value.dateTo, "yyyy-MM-dd")
      : null;

    const filters = {
      dateFrom: dateFrom,
      dateTo: dateTo,
    };

   // console.log("getTotalComplaintsByCategoryCount filters:", filters);

    this.appDataService.getTotalComplaintsByCategoryCount(filters).subscribe({
      next: (response: any) => {
       // console.log("getTotalComplaintsByCategoryCount response:", response);

        this.totalCountofComplaintbyCategoryChartDataCategories = [];

        this.totalCountofComplaintbCategoryChartData =
          response?.data?.map((element: any[]) => {
            this.totalCountofComplaintbyCategoryChartDataCategories.push(
              element[1]
            );
            return {
              name: element[1],
              // y: element[0],
              y: 1,
            };
          }) || [];

        // console.log(
        //   "Pie chart data:",
        //   this.totalCountofComplaintbCategoryChartData
        // );

        this.setOptions();
        this.renderDashboard();
      },
      error: (error) =>
        this.handleApiError(error, "getTotalComplaintsByCategoryCount"),
    });
  }

  setDateRange(range: "3d" | "1w" | "2w" | "1m"): void {
    this.isLoading = true;
    const today = new Date();
    let fromDate = new Date();

    switch (range) {
      case "3d":
        fromDate.setDate(today.getDate() - 3);
        break;
      case "1w":
        fromDate.setDate(today.getDate() - 7);
        break;
      case "2w":
        fromDate.setDate(today.getDate() - 14);
        break;
      case "1m":
        fromDate.setMonth(today.getMonth() - 1);
        break;
    }

    // console.log(
    //   `Setting date range: ${range}, from: ${fromDate}, to: ${today}`
    // );

    // FIXED: Reset selected category and user when changing date range
    this.selectedCategory = "";
    this.selectedUserName = "";

    this.form.patchValue(
      {
        dateFrom: fromDate,
        dateTo: today,
      },
      { emitEvent: false }
    );

    this.resetPagination();

    // FIXED: Add a small delay to ensure form values are properly set
    setTimeout(() => {
      this.loadAllData();
      this.isLoading = false;
    }, 100);
  }

  // UPDATED: Modified to use the new updateDetailsDataSource method
  getTotalComplaintsByAgentDetailCount(): void {
    const dateFrom = this.form.value.dateFrom
      ? this.datePipe.transform(this.form.value.dateFrom, "yyyy-MM-dd")
      : null;
    const dateTo = this.form.value.dateTo
      ? this.datePipe.transform(this.form.value.dateTo, "yyyy-MM-dd")
      : null;

    const filters = {
      dateFrom: dateFrom,
      dateTo: dateTo,
    };

   // console.log("getTotalComplaintsByAgentDetailCount filters:", filters);

    this.appDataService
      .getTotalComplaintsByAgentDetailCount(filters)
      .subscribe({
        next: (response: any) => {
          const data = response.data ?? [];
         // console.log("getTotalComplaintsByAgentDetailCount response:", data);

          const formattedData = data?.map((res: any) => {
            res[3] = this.formatDate(res[3]); // receivedDate at index 3
            res[4] = this.formatDate(res[4]); // closedDate at index 4
            res[5] = this.formatDuration(res[5]); // avgResponseTime at index 5 (FIXED)
            return res;
          });

          //console.log("Formatted detail count data:", formattedData);
          this.updateDetailsDataSource(formattedData);
          this.setOptions();
          this.renderDashboard();
        },
        error: (error) =>
          this.handleApiError(error, "getTotalComplaintsByAgentDetailCount"),
      });
  }

  // UPDATED: Modified to use the new updateDetailsDataSource method
  getAgentDetailCount(category: string, complaintType?: string): void {
    const dateFrom = this.form.value.dateFrom
      ? this.datePipe.transform(this.form.value.dateFrom, "yyyy-MM-dd")
      : null;
    const dateTo = this.form.value.dateTo
      ? this.datePipe.transform(this.form.value.dateTo, "yyyy-MM-dd")
      : null;

    const data = {
      name: complaintType,
      complaintType: category,
      dateFrom: dateFrom,
      dateTo: dateTo,
    };

   // console.log("getAgentDetailCount data:", data);

    this.appDataService.getTotalComplaintsByAgentDetailCount(data).subscribe({
      next: (response: any) => {
        const data = response.data ?? [];
      //  console.log("getAgentDetailCount response:", data);

        const formattedData = data?.map((res: any) => {
          res[3] = this.formatDate(res[3]);
          res[4] = this.formatDate(res[4]);
          res[5] = this.formatDuration(res[5]);
          return res;
        });

      //  console.log("Formatted agent detail data:", formattedData);
        this.updateDetailsDataSource(formattedData);
        this.setOptions();
        this.renderDashboard();
      },
      error: (error) => this.handleApiError(error, "getAgentDetailCount"),
    });
  }


  clearDate(controlName: 'dateFrom' | 'dateTo'): void {
    this.form.get(controlName)?.setValue(null);
  }

  sortCSR(column: string): void {
    // Cycle direction: '' → 'asc' → 'desc' → ''
    if (this.csrSortState.column !== column) {
      this.csrSortState = { column, direction: 'asc' };
    } else {
      if (this.csrSortState.direction === '') {
        this.csrSortState.direction = 'asc';
      } else if (this.csrSortState.direction === 'asc') {
        this.csrSortState.direction = 'desc';
      } else {
        this.csrSortState = { column: '', direction: '' }; // reset
      }
    }

    // Apply sorting
    if (this.csrSortState.direction === '') {
      this.csrDataSource.data = [...this.originalCSRData];
    } else {
      const columnIndexMap: { [key: string]: number } = {
        csr: 0,
        totalComplaints: 1,
        avgResponseTime: 2,
      };
      const index = columnIndexMap[column];
      const asc = this.csrSortState.direction === 'asc';

      this.csrDataSource.data = [...this.csrDataSource.data].sort((a, b) => {
        const valA = a[index];
        const valB = b[index];

        // Handle number sorting (totalComplaints, avgResponseTime) vs string (csr)
        const isNumber = column !== 'csr';
        const aVal = isNumber ? +valA : (valA || '').toLowerCase();
        const bVal = isNumber ? +valB : (valB || '').toLowerCase();

        if (aVal < bVal) return asc ? -1 : 1;
        if (aVal > bVal) return asc ? 1 : -1;
        return 0;
      });
    }
  }
  // Respondent sort for Details table
  isDetailsByRespondentAsc: boolean = true;
  sortDetailsByRespondent(): void {
    // cycle 0 → 1 → 2 → 0 …
    this.sortStateDetailsByRespondent = ((this.sortStateDetailsByRespondent +
      1) %
      3) as 0 | 1 | 2;

    if (this.sortStateDetailsByRespondent === 0) {
      // reset original order
      this.detailsDataSource.data = [...this.originalDetailsData];
    } else {
      const asc = this.sortStateDetailsByRespondent === 1;
      this.detailsDataSource.data = [...this.detailsDataSource.data].sort(
        (a, b) => {
          const nameA = (a[0] || "").toLowerCase();
          const nameB = (b[0] || "").toLowerCase();
          return asc ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
        }
      );
    }
  }

  // Complainant Type sort
  isDetailsByComplainantTypeAsc: boolean = true;
  sortDetailsByComplainantType(): void {
    // cycle 0 → 1 → 2 → 0 …
    this.sortStateDetailsByType = ((this.sortStateDetailsByType + 1) % 3) as
      | 0
      | 1
      | 2;

    if (this.sortStateDetailsByType === 0) {
      // reset to original
      this.detailsDataSource.data = [...this.originalDetailsData];
    } else {
      const asc = this.sortStateDetailsByType === 1;
      this.detailsDataSource.data = [...this.detailsDataSource.data].sort(
        (a, b) => {
          // a[6] and b[6] should correspond to your complainantType column index
          const typeA = (a[6] || "").toLowerCase();
          const typeB = (b[6] || "").toLowerCase();
          return asc ? typeA.localeCompare(typeB) : typeB.localeCompare(typeA);
        }
      );
    }
  }
  // Complaint Type sort
  isDetailsByComplaintTypeAsc: boolean = true;
  sortDetailsByComplaintType(): void {
    // cycle 0 → 1 → 2 → 0 …
    this.sortStateDetailsByComplaintType = ((this
      .sortStateDetailsByComplaintType +
      1) %
      3) as 0 | 1 | 2;

    if (this.sortStateDetailsByComplaintType === 0) {
      // reset
      this.detailsDataSource.data = [...this.originalDetailsData];
    } else {
      const asc = this.sortStateDetailsByComplaintType === 1;
      this.detailsDataSource.data = [...this.detailsDataSource.data].sort(
        (a, b) => {
          const typeA = (a[7] || "").toLowerCase();
          const typeB = (b[7] || "").toLowerCase();
          return asc ? typeA.localeCompare(typeB) : typeB.localeCompare(typeA);
        }
      );
    }
  }

  formatDate(date: string): string {
    if (!date) return "";

    const formattedDate = new Date(date);

    if (isNaN(formattedDate.getTime())) {
      return "";
    }

    const day = formattedDate.getDate().toString().padStart(2, "0");
    const month = (formattedDate.getMonth() + 1).toString().padStart(2, "0");
    const year = formattedDate.getFullYear();
    const hours = formattedDate.getHours().toString().padStart(2, "0");
    const minutes = formattedDate.getMinutes().toString().padStart(2, "0");

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  }

  formateDate(date: string): string {
    return this.formatDate(date);
  }

  private handleApiError(error: any, apiName: string): void {
    console.error(`Error in ${apiName}:`, error);
  }

  private setOptions() {
    this.options = {
      dataPool: {},
      gui: {
        layouts: [
          {
            id: "pie-chart-ticket-by-ticket-type-col-0",
          },
        ],
      },
      components: [
        {
          sync: {
            visibility: true,
            highlight: true,
            extremes: true,
          },
          renderTo: "pie-chart-ticket-by-ticket-type-col-0",
          type: "Highcharts",
          chart: {
            type: "pie",
          },
          chartOptions: {
            title: {
              text: '<h2 style="font-weight: 500">Complaint By Category</h2>',
              align: "left",
              useHTML: true,
            },
            chart: {
              type: "pie",
              backgroundColor: "white",
            },
            tooltip: {
              enabled: false,
            },
            legend: {
              enabled: true,
            },
            plotOptions: {
              series: {
                borderWidth: 0,
                colorByPoint: true,
                type: "pie",
                size: "100%",
                innerSize: "70%",
                dataLabels: {
                  enabled: true,
                  crop: false,
                  distance: "10%",
                  style: {
                    fontWeight: "bold",
                    fontSize: "16px",
                  },
                  connectorWidth: 0,
                },
                showInLegend: true,
                point: {
                  events: {
                    click: this.onSliceClick.bind(this),
                  },
                },
              },
            },
            series: [
              {
                type: "pie",
                name: "Total Complaints",
                colorByPoint: true,
                data: this.totalCountofComplaintbCategoryChartData,
              },
            ],
          },
        },
      ],
    };
  }

  loadUserid() {
    const permissions = localStorage.getItem("permissions");
    if (permissions) {
      try {
        const parsedPermissions = JSON.parse(permissions);
        const getUserRole = parsedPermissions.role;
        this.userRole = getUserRole;
      } catch (error) {
        console.error("Error parsing localStorage data:", error);
      }
    }
  }
}
